import React, { useMemo, useState, createElement } from 'react';
import { Download, Search, Calendar, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { orderPaymentBreakdowns } from '../../data/reportMockData';
export function OrderPaymentBreakdownReport() {
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState('2024-01-01');
  const [dateTo, setDateTo] = useState('2024-12-31');
  const [appliedFrom, setAppliedFrom] = useState('2024-01-01');
  const [appliedTo, setAppliedTo] = useState('2024-12-31');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const filtered = useMemo(() => {
    return orderPaymentBreakdowns.filter(r => r.date >= appliedFrom && r.date <= appliedTo);
  }, [appliedFrom, appliedTo]);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handleSearch = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setCurrentPage(1);
  };
  const handleDownload = () => {
    const headers = ['Date', 'Payer Name', 'Payer NID', 'Total Amount', 'Court Fee', 'Court Fee Challan No', 'Court Fee Challan Date', 'Postal Fee', 'Postal Fee Challan No', 'Postal Fee Challan Date', 'Stationary Fee', 'Stationary Fee Challan No', 'Stationary Fee Challan Date', 'Letter Writer Fee', 'EFT Number', 'EFT Date', 'Order Number', 'Transaction ID', 'Service Type'];
    const rows = filtered.map(r => [r.date, r.payerName, r.payerNid, r.totalAmount, r.courtFee, r.courtFeeChallanNumber, r.courtFeeChallanDate, r.postalFee, r.postalFeeChallanNumber, r.postalFeeChallanDate, r.stationaryFee, r.stationaryFeeChallanNumber, r.stationaryFeeChallanDate, r.letterWriterFee, r.letterWriterEftNumber, r.letterWriterEftDate, r.orderNumber, r.transactionId, r.serviceType]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-payment-breakdown-${appliedFrom}-to-${appliedTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-287">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-288">
        <div data-id="element-289">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-290">
            Order Payment Breakdown Report
          </h1>
          <p className="text-slate-500" data-id="element-291">
            Detailed fee breakdown per order transaction
          </p>
        </div>
        <Button onClick={handleDownload} variant="secondary" data-id="element-292">
          <Download className="mr-2 h-4 w-4" data-id="element-293" /> Download CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-id="element-294">
        <div className="flex flex-wrap items-end gap-4" data-id="element-295">
          <div data-id="element-296">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-297">
              From Date
            </label>
            <div className="relative" data-id="element-298">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-299" />
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-300" />
            </div>
          </div>
          <div data-id="element-301">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-302">
              To Date
            </label>
            <div className="relative" data-id="element-303">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-304" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-305" />
            </div>
          </div>
          <Button onClick={handleSearch} data-id="element-306">
            <Search className="mr-2 h-4 w-4" data-id="element-307" /> Search
          </Button>
          <div className="ml-auto text-sm text-slate-500 self-center" data-id="element-308">
            <span className="font-semibold text-slate-800" data-id="element-309">
              {filtered.length}
            </span>{' '}
            records found
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-id="element-310">
        <div className="overflow-x-auto" data-id="element-311">
          <table className="w-full text-sm text-left" data-id="element-312">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-313">
              <tr data-id="element-314">
                {/* Basic Info */}
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-315">
                  Date
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-316">
                  Payer Name
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-317">
                  Payer NID
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-318">
                  Total (৳)
                </th>
                {/* Court Fee */}
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-319">
                  Court Fee (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-320">
                  Challan No.
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-321">
                  Challan Date
                </th>
                {/* Postal Fee */}
                <th className="px-4 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider whitespace-nowrap bg-orange-50" data-id="element-322">
                  Postal Fee (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider whitespace-nowrap bg-orange-50" data-id="element-323">
                  Challan No.
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider whitespace-nowrap bg-orange-50" data-id="element-324">
                  Challan Date
                </th>
                {/* Stationary Fee */}
                <th className="px-4 py-3 text-xs font-semibold text-purple-600 uppercase tracking-wider whitespace-nowrap bg-purple-50" data-id="element-325">
                  Stationary Fee (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-purple-600 uppercase tracking-wider whitespace-nowrap bg-purple-50" data-id="element-326">
                  Challan No.
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-purple-600 uppercase tracking-wider whitespace-nowrap bg-purple-50" data-id="element-327">
                  Challan Date
                </th>
                {/* Letter Writer Fee */}
                <th className="px-4 py-3 text-xs font-semibold text-teal-600 uppercase tracking-wider whitespace-nowrap bg-teal-50" data-id="element-328">
                  Letter Writer Fee (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-teal-600 uppercase tracking-wider whitespace-nowrap bg-teal-50" data-id="element-329">
                  EFT Number
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-teal-600 uppercase tracking-wider whitespace-nowrap bg-teal-50" data-id="element-330">
                  EFT Date
                </th>
                {/* Order Info */}
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-331">
                  Order No.
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-332">
                  Transaction ID
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-333">
                  Service
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-334">
              {paginated.length > 0 ? paginated.map(row => <tr key={row.id} className="hover:bg-slate-50 transition-colors" data-id="element-335">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-336">
                      {row.date}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" data-id="element-337">
                      {row.payerName}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600" data-id="element-338">
                      {row.payerNid}
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900" data-id="element-339">
                      ৳{row.totalAmount}
                    </td>
                    {/* Court Fee */}
                    <td className="px-4 py-3 bg-blue-50/50 text-blue-800 font-medium" data-id="element-340">
                      ৳{row.courtFee}
                    </td>
                    <td className="px-4 py-3 bg-blue-50/50 font-mono text-xs text-slate-600" data-id="element-341">
                      {row.courtFeeChallanNumber}
                    </td>
                    <td className="px-4 py-3 bg-blue-50/50 text-slate-600 whitespace-nowrap" data-id="element-342">
                      {row.courtFeeChallanDate}
                    </td>
                    {/* Postal Fee */}
                    <td className="px-4 py-3 bg-orange-50/50 text-orange-800 font-medium" data-id="element-343">
                      ৳{row.postalFee}
                    </td>
                    <td className="px-4 py-3 bg-orange-50/50 font-mono text-xs text-slate-600" data-id="element-344">
                      {row.postalFeeChallanNumber}
                    </td>
                    <td className="px-4 py-3 bg-orange-50/50 text-slate-600 whitespace-nowrap" data-id="element-345">
                      {row.postalFeeChallanDate}
                    </td>
                    {/* Stationary Fee */}
                    <td className="px-4 py-3 bg-purple-50/50 text-purple-800 font-medium" data-id="element-346">
                      ৳{row.stationaryFee}
                    </td>
                    <td className="px-4 py-3 bg-purple-50/50 font-mono text-xs text-slate-600" data-id="element-347">
                      {row.stationaryFeeChallanNumber}
                    </td>
                    <td className="px-4 py-3 bg-purple-50/50 text-slate-600 whitespace-nowrap" data-id="element-348">
                      {row.stationaryFeeChallanDate}
                    </td>
                    {/* Letter Writer Fee */}
                    <td className="px-4 py-3 bg-teal-50/50 text-teal-800 font-medium" data-id="element-349">
                      ৳{row.letterWriterFee}
                    </td>
                    <td className="px-4 py-3 bg-teal-50/50 font-mono text-xs text-slate-600" data-id="element-350">
                      {row.letterWriterEftNumber}
                    </td>
                    <td className="px-4 py-3 bg-teal-50/50 text-slate-600 whitespace-nowrap" data-id="element-351">
                      {row.letterWriterEftDate}
                    </td>
                    {/* Order Info */}
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-352">
                      {row.orderNumber}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-353">
                      {row.transactionId}
                    </td>
                    <td className="px-4 py-3" data-id="element-354">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.serviceType === 'Khatian' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-355">
                        {row.serviceType}
                      </span>
                    </td>
                  </tr>) : <tr data-id="element-356">
                  <td colSpan={19} className="px-4 py-12 text-center text-slate-400" data-id="element-357">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" data-id="element-358" />
                    No records found for the selected date range.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-slate-500" data-id="element-359">
          <span data-id="element-360">
            Showing{' '}
            {Math.min((currentPage - 1) * rowsPerPage + 1, filtered.length)}–
            {Math.min(currentPage * rowsPerPage, filtered.length)} of{' '}
            {filtered.length}
          </span>
          <div className="flex items-center space-x-1" data-id="element-361">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-362">
              Previous
            </button>
            {Array.from({
            length: Math.min(5, totalPages)
          }, (_, i) => {
            const page = i + 1;
            return <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded border transition-colors ${currentPage === page ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 hover:bg-white'}`} data-id="element-363">
                    {page}
                  </button>;
          })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-364">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}